import {
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import _ from 'lodash';
import {
  StockLocation,
  StockLocationType,
  StockLocationTypeLabels,
} from 'types/inventory';
import { ExtendedGridColDef } from 'types/datagrid';
import { ALL_APP_IDS, ALL_APPS } from 'constants/appConstants';
import { longTextWithEllipsisRenderCell } from 'ui-component/DataGrid/Render';

const typeValueOptions = Object.entries(StockLocationTypeLabels).map(
  ([value, label]) => ({
    value: value as StockLocationType,
    label,
  })
);

const makeColumns = ({
  stockLocations,
  handleReplaceWithCustomTeamName,
}: {
  stockLocations?: StockLocation[];
  handleReplaceWithCustomTeamName: (inputString: string) => string;
}): ExtendedGridColDef[] => [
  {
    field: 'locationType',
    headerName: 'Type',
    description: 'Type of storage location',
    type: 'singleSelect',
    minWidth: 80,
    flex: 0.5,
    hideable: true,
    valueOptions: typeValueOptions,
    valueFormatter: ({ value }: GridValueFormatterParams) => {
      const option = _.find(typeValueOptions, ['value', value]);
      if (option) {
        return option.label;
      }
      return '-';
    },
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'stockLocationCode',
    headerName: 'Stock Location Code',
    description: 'Stock location code of the storage location',
    type: 'string',
    minWidth: 80,
    flex: 1,
    permissionScope: { app: ALL_APPS.KITTING.id },
    hideable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    description: 'Name of the storage location',
    type: 'string',
    minWidth: 80,
    flex: 1,
    hideable: false,
    valueGetter: (params: GridValueGetterParams) =>
      // externalName seems to have format the string already, so, use 'name' only when 'externalName' does not exist
      params.row.externalName || params.row.name,
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'facility',
    headerName: 'Address',
    description: 'Address that this storage location is within',
    type: 'singleSelect',
    // Note: will need to change this to operate on empty string and non start case
    // location names if valueGetter is ever changed to valueFormatter... it PROBABLY
    // should be a valueFormatter but whatever
    valueOptions: stockLocations
      ?.map((location) => location.externalName || _.startCase(location.name))
      .concat(['-']),
    minWidth: 80,
    flex: 1,
    hideable: true,
    valueGetter: (params: GridValueGetterParams) =>
      // externalName seems to have format the string already, so, use 'name' only when 'externalName' does not exist
      params.row.facility?.externalName ||
      _.startCase(params.row.facility?.name) ||
      '-',
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'parent',
    headerName: 'Parent Location',
    description: 'Parent location of this storage location',
    type: 'singleSelect',
    valueOptions: stockLocations
      ?.map((location) => location.externalName || _.startCase(location.name))
      .concat(['-']),
    minWidth: 80,
    flex: 1.5,
    hideable: true,
    valueGetter: (params: GridValueGetterParams) =>
      params.row?.parent?.externalName ||
      _.startCase(params.row?.parent?.name) ||
      '-',
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'notificationEmail',
    headerName: 'Notification Email',
    type: 'string',
    flex: 0.5,
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'id',
    headerName: 'ID',
    description: 'Cofactr ID of the storage location',
    type: 'string',
    minWidth: 80,
    flex: 1,
    permissionScope: { app: ALL_APPS.KITTING.id },
  },
  {
    field: 'team',
    headerName: handleReplaceWithCustomTeamName('Team'),
    description: handleReplaceWithCustomTeamName(
      'Team associated with this Stock Location'
    ),
    permissionScope: { app: ALL_APP_IDS.PROPERTIES },
    valueGetter: (params: GridValueGetterParams) =>
      params.row.team?.name ?? '-',
    renderCell: longTextWithEllipsisRenderCell,
  },
];

export default makeColumns;
